/**
 * The dashboard banner (punch 4.6; docs/11 D-5).
 *
 * v1 had a `PortfolioHeroBanner` — a time-of-day hero with slot-level and
 * global IMAGE fallbacks. Matt asked for it back (2026-08-27), and what returns
 * is the calm half: the greeting, the day, and the mark, on the same evergreen
 * the sidebar already is. Deliberately NOT the graphical half, for two reasons
 * worth stating where somebody would otherwise re-add it:
 *
 *   * docs/04 §6 permits imagery as the S3 exception only — the property photo
 *     and the avatar — and forbids decorative illustration outright. A stock
 *     band would need that rule amended first, not worked around.
 *   * an administered image is a settings table, an upload path, an admin
 *     screen and a permission. docs/10 records what that would take. It is
 *     DEFERRED ARCHITECTURE, not a schema anybody has approved, and this
 *     component deliberately carries no prop, seam or placeholder for it —
 *     an unused prop is a promise the next session would read as a plan.
 *
 * WHAT IT IS NOT, so the band does not grow:
 *
 *   * no portfolio count and no action-item count. The Action items card sits
 *     directly below with the real read behind it; a number up here would
 *     either duplicate it or, worse, assert something nothing looked up (the
 *     O-28 trap this dashboard already fell into once).
 *   * no image, no animation, no scroll behaviour. ADR-28 rejected
 *     shrink-on-scroll for the property header and the reasoning is the same:
 *     a collapsing band leaves the first screenful just as crowded until you
 *     scroll, and scroll-linked animation fights reduced-motion.
 *
 * It carries the page's ONLY `<h1>`. The greeting was already the h1 on this
 * screen; it moved inside the band rather than gaining a sibling.
 *
 * Under View As the name is the PERSON BEING VIEWED — that is what View As
 * means, and the impersonation banner sits above this one saying whose eyes
 * these are (docs/01 §6).
 */

import type { ReactNode } from 'react';

import { BrandMark } from '../../components/ui/index.js';

import { bannerDate, greetingFor } from './greeting.js';

export function DashboardBanner({ displayName }: { readonly displayName: string }): ReactNode {
  // No injectable clock. The greeting's three boundaries are unit-tested on the
  // pure function; a `now` prop nothing passes would be the same unused seam
  // this component refuses for the graphical banner.
  const now = new Date();
  // First name only. "Good morning, Matthew Keith" reads like a letter from the
  // bank; the app is greeting a colleague.
  const first = displayName.split(/\s+/)[0] ?? displayName;
  return (
    <div
      className={
        // Same surface as the sidebar, so the dashboard reads as one piece with
        // the chrome rather than as a second brand. Both tokens are already
        // asserted at 4.5:1 in BOTH themes (scripts/ci/contrast.ts:
        // "on-green primary" and "on-green sub" on --green-950), so this adds
        // no colour and no new pair.
        'flex flex-col gap-3 rounded-card bg-brand-950 px-4 py-4 ' +
        'sm:flex-row sm:items-center sm:justify-between sm:px-6 sm:py-5'
      }
    >
      <div className="min-w-0">
        {/* text-balance so a long first name wraps evenly at 375px rather than
            dropping one word onto its own line. */}
        <h1 className="text-balance font-display text-display-sm font-semibold text-on-green sm:text-display">
          {greetingFor(now)}, {first}
        </h1>
        <p className="mt-1 text-meta text-on-green-sub">{bannerDate(now)}</p>
      </div>
      {/* The mark trails the greeting on a wide viewport and is absent below
          640: at 375 the sidebar is a bottom bar and the mark is in the top
          bar, so a third copy on the same screen is noise, not branding. */}
      <span className="hidden sm:block">
        <BrandMark size="md" />
      </span>
    </div>
  );
}
